<script>
import { mapState } from 'vuex';
import moment from 'moment';
import { getVisitBookedForDate } from '@/util/helpers';
import SocialVisitsService from '@/services/SocialVisitsService';
import { getAdditionalVisitorsTextForBookingSlot, getKioskItemsText } from '@/util/PrintHelpers';
import VisitLocationsService from '@/services/VisitLocationsService';

export default {
  name: 'PrintSocialVisits',
  props: {},
  data() {
    return {
      loading: true,
      date: '',
      visits: [],
      visitLocations: [],
      selectedVisitLocationId: '',
      printing: false,
    };
  },
  computed: {
    ...mapState({
      prison: state => state.prison.prison,
    }),
    visitLocationOptions() {
      const locations = [{ value: '', text: 'All' }];
      const mappedLocations = this.visitLocations.map(location => ({
        value: location._id,
        text: location.name,
      }));
      return locations.concat(mappedLocations);
    },
    selectedVisitLocation() {
      return this.visitLocations.find(l => l._id === this.selectedVisitLocationId)
    },
  },
  async mounted() {
    try {
      await Promise.all([
        this.$store.dispatch('prison/getPrison'),
        this.getVisitLocations(),
      ]);
      this.loading = false;
    } catch (e) {
      console.error(e);
    }
  },
  methods: {
    getKioskItemsText,
    async getVisitLocations() {
      try {
        const response = await VisitLocationsService.getVisitLocationsForPrison();
        this.visitLocations = response.data;
      } catch (e) {
        console.error('Error getting visit locations: ', e);
        const res = e.response;
        let errorText = 'Error fetching visit locations, please refresh and try again.';
        if (res && res.data.error) {
          errorText = res.data.error;
        }
        this.$toast.error(errorText);
        throw e;
      }
    },
    getVisits() {
      SocialVisitsService.getApprovedVisits(this.selectedVisitLocationId).then(res => {
        this.visits = this.orderVisits(res.data).map(visit => {
          const bookedFor = getVisitBookedForDate(visit);
          return {
            bookedFor: bookedFor.bookedFor,
            bookedForDate: bookedFor.bookedForDate,
            ...visit,
          };
        });
        this.loading = false;
      }).catch(() => {
        this.$toast.error('Could not get visits, please refresh and try again', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      });
    },
    orderVisits(visits) {
      if (!!visits && visits.length > 0) {
        return visits.sort((v1, v2) => {
          const v1StartDate = new Date(v1.booked_slot.start_date_time);
          const v2StartDate = new Date(v2.booked_slot.start_date_time);
          // Compare dates
          return v1StartDate.getTime() - v2StartDate.getTime();
        });
      }
      return [];
    },
    filteredVisits() {
      let filteredVisits = [...this.visits];
      if (this.date) {
        const beforeDate = moment(this.date).format();
        filteredVisits = filteredVisits.filter(visit => moment(visit.bookedForDate).isSame(beforeDate, 'day'));
      }

      const approvedVisits = filteredVisits.filter(visit => visit.status === 'Approved');
      const groupedData = [];

      approvedVisits.forEach(visit => {
        groupedData.push({
          bookingSlotId: visit.booked_slot.booking_slot_id,
          bookingSlotName: `Booking slot ${visit.booked_slot.slot_begin} - ${visit.booked_slot.slot_end}`,
          visitInfo: [`${visit.prisoner.firstname} ${visit.prisoner.lastname} ${visit.prisoner.prisoner_number} - ${visit.visitor.firstname} ${visit.visitor.lastname} ${getAdditionalVisitorsTextForBookingSlot(visit.additional_visitors)}`],
          kioskItems: visit.kiosk_items || [],
          visitLocation: visit.visit_location.name,
        });
      });

      return groupedData;
    },
    print() {
      this.printing = true;
      this.$store.commit('verticalMenu/UPDATE_VERTICAL_MENU_COLLAPSED', true);
      window.addEventListener('afterprint', this.handleAfterPrint);
      setTimeout(() => {
        window.print();
      }, 500);
    },
    handleAfterPrint() {
      this.printing = false;
      this.$store.commit('verticalMenu/UPDATE_VERTICAL_MENU_COLLAPSED', false);
      window.removeEventListener('afterprint', this.handleAfterPrint);
    },
    showPrintButton() {
      return this.filteredVisits(this.visits).length > 0;
    },
    clearDateAndVisits() {
      this.date = '';
      this.visits = [];
    },
  },
};
</script>

<template>
  <b-card>
    <div class="row">
      <div class="col-12">
        <h2 cy="page-title" class="font-weight-bolder mb-2 full-width">{{ printing ? '' : 'Print' }} Approved Social Visits</h2>

        <section v-if="!loading">
          <section v-if="prison">
            <b-row v-if="!printing">
              <b-col cols="3">
                <label>
                  Visit Location
                </label>
                <b-form-select
                  v-model="selectedVisitLocationId"
                  :options="visitLocationOptions"
                  placeholder="Select location"
                  @change="clearDateAndVisits"
                />
              </b-col>
              <b-col cols="3">
                <label>Please enter a date for social visits to print</label>
                <b-form-datepicker
                  v-model="date"
                  :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                  close-button

                  reset-button
                  today-button
                  @input="getVisits()"
                />
              </b-col>
            </b-row>

            <b-row v-if="!printing" class="mt-2">
              <b-col v-if="date" cols="3">
                <b-btn variant="primary" class="full-width" @click="getVisits()">
                  <i class="fa fa-sync" style="margin-right: 5px" /> Refresh Visits
                </b-btn>
              </b-col>
              <b-col cols="3">
                <b-btn v-if="date" class="full-width" :class="{'disabled': !showPrintButton()}" variant="success" @click="print()">
                  <span class="fa fa-print" style="margin-right: 5px" /> Print Visits
                </b-btn>
              </b-col>
            </b-row>

            <b-row v-if="printing">
              <b-col cols="3">
                <h3>Location</h3>
                {{ selectedVisitLocation ? selectedVisitLocation.name : 'All locations' }}
              </b-col>
              <b-col cols="3">
                <h3>Date</h3>
                {{ date | formatDate }}
              </b-col>
            </b-row>

            <div v-if="date" class="mt-2">
              <div v-if="filteredVisits().length > 0">
                <div v-for="(item, index) in filteredVisits()" :key="index" class="row mt-3">
                  <div class="col-12">
                    <h3><b>{{ item.bookingSlotName }}</b></h3>
                    <p>Location: <b>{{ item.visitLocation }}</b></p>
                    <p v-for="(visitInfo, visitIndex) in item.visitInfo" :key="visitIndex">{{ visitInfo }}</p>
                    <div v-if="prison.allow_kiosk_social && item.kioskItems.length > 0">
                      <h4><b>Kiosk Items</b></h4>
                      <p>{{ getKioskItemsText(item.kioskItems) }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else>
                <p>No visits approved for this date.</p>
              </div>
            </div>
          </section>

          <section v-else>
            <p>There was a problem getting your visit data, please refresh and try again</p>
          </section>
        </section>

        <b-spinner v-else variant="primary" label="Loading call" />
      </div>
    </div>
  </b-card>
</template>
